import axios from "axios";
import * as Yup from "yup";
import { EMAIL_REGEX } from "@/func/emailValidation";

interface LeadData {
  email: string;
  first_name: string;
  product_name?: string;
  product_qty?: number;
}

class EmailMarketingLeadsProvider {
  private baseUrl: string;
  private sessionId: string | null;
  private validationSchema: Yup.ObjectSchema<LeadData>;

  constructor() {
    this.baseUrl = "/wp-json/api/v1/email-marketing";
    this.sessionId = sessionStorage.getItem("sessionId") || null;

    this.validationSchema = Yup.object().shape({
      email: Yup.string().email().matches(EMAIL_REGEX).required(),
      first_name: Yup.string().min(1).required(),
      product_name: Yup.string().required(),
      product_qty: Yup.number().min(1).required(),
    }) as Yup.ObjectSchema<LeadData>;
  }

  validate(data: LeadData): boolean {
    try {
      this.validationSchema.validateSync(data, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }

  async initialize(): Promise<string> {
    if (this.sessionId) {
      return this.sessionId;
    }

    try {
      const { data } = await axios.get(`${this.baseUrl}/session`);
      this.sessionId = data.sessionId;
      sessionStorage.setItem("sessionId", this.sessionId!);
      return this.sessionId!;
    } catch {
      throw new Error("Unable to initialize session. Please try again later.");
    }
  }

  async createLead(
    leadData: LeadData,
  ): Promise<{ status: string; session_id: string; email: string }> {
    if (!this.sessionId) {
      throw new Error(
        "Session not available. Please refresh the page and try again.",
      );
    }

    try {
      const { data } = await axios.post(
        `${this.baseUrl}/createLead`,
        leadData,
        {
          headers: {
            "X-Session-Id": this.sessionId,
          },
        },
      );
      return data;
    } catch {
      throw new Error(
        "Failed to process your request. Please check your information and try again.",
      );
    }
  }
}

export default EmailMarketingLeadsProvider;
